import type { AppContext } from '@application/context/AppContext';
import { TenantProvisioningService } from '@application/licence/TenantProvisioningService';
import { countUsersForTenant } from '@application/licence/LicenceAccountGuard';
import { isCloudWebPlaceholderTenant } from '@application/licence/CloudWebHost';
import { isHybridDeployment, isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { isDemoSeedEnabled } from '@infra/licence/demoSeedConfig';
import { LicensingSessionService } from '@infra/licence/LicensingSessionService';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import { logger } from '@infra/telemetry/Logger';
import { isValidBusinessEmail } from '@shared/validation/email';

export function validateFirstAdminInput(input: {
  prenom: string;
  nom: string;
  email: string;
  password: string;
  passwordConfirm: string;
}): string | null {
  const prenom = input.prenom.trim();
  const nom = input.nom.trim();
  const email = input.email.trim();
  if (prenom.length < 2 || nom.length < 2) {
    return 'Indiquez le prénom et le nom (2 caractères minimum).';
  }
  if (!isValidBusinessEmail(email)) {
    return 'Indiquez une adresse email valide avec extension de domaine (ex. nom@entreprise.ga).';
  }
  if (input.password.length < 12 || input.password.length > 128) {
    return 'Choisissez un mot de passe de 12 à 128 caractères (une phrase de passe est idéale).';
  }
  if (input.password !== input.passwordConfirm) {
    return 'Les deux saisies du mot de passe doivent être identiques.';
  }
  return null;
}

/**
 * Indique si l'écran « premier administrateur » doit s'afficher.
 * Mode distant : interroge la business-api via le jeton d'installation.
 */
export async function resolveNeedsFirstAdmin(
  ctx: AppContext,
  licenceTenantId: string | undefined,
): Promise<boolean> {
  if (!licenceTenantId) {
    return false;
  }

  if (TenantProvisioningService.isDemoTenant(licenceTenantId)) {
    // Dev navigateur : bypass licence sur tenant démo, seed désactivé → premier admin.
    if (
      import.meta.env.DEV &&
      !isDemoSeedEnabled() &&
      (await countUsersForTenant(ctx, licenceTenantId)) === 0
    ) {
      return true;
    }
    return false;
  }

  // Navigateur Cloud sans licence activée : tenant provisoire (licence factice) → jamais de formulaire.
  if (isCloudWebPlaceholderTenant(licenceTenantId)) {
    return false;
  }

  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    const sessionOk = await LicensingSessionService.ensureInstallationSession(licenceTenantId);
    if (!sessionOk) {
      logger.warn({ event: 'needs_first_admin_installation_session_failed' });
      // Échec fermé : sans session d'installation (pas de licence activée / serveur injoignable),
      // le formulaire « Premier administrateur » ne doit JAMAIS s'afficher (écran de connexion/activation).
      return false;
    }
    try {
      const status = await getBusinessApiClient().getBootstrapStatus();
      return status.needs_first_admin;
    } catch (err) {
      logger.warn({ event: 'bootstrap_status_unavailable', error: String(err) });
      // Échec fermé : statut inconnu → pas de création de compte administrateur.
      return false;
    }
  }

  // Offre Hybride : les comptes sont ceux du serveur. Serveur joignable -> c'est lui qui décide ; injoignable ->
  // jamais de compte local isolé (échec fermé), la connexion hors ligne s'appuie sur l'identité déjà mémorisée.
  if (isHybridDeployment(ctx.getLicenceRuntime()?.getState()?.claims ?? null)) {
    try {
      const sessionOk = await LicensingSessionService.ensureInstallationSession(licenceTenantId);
      if (!sessionOk) return false;
      const status = await getBusinessApiClient().getBootstrapStatus();
      return status.needs_first_admin;
    } catch (err) {
      logger.warn({ event: 'hybrid_bootstrap_status_unavailable', error: String(err) });
      return false;
    }
  }

  return (await countUsersForTenant(ctx, licenceTenantId)) === 0;
}
